export async function HashU(inputString: string): Promise<string> {
  /**
     * Hashes a string using the SHA-256 algorithm.
     *
     * @param inputString The string to be hashed.
     * @returns A promise that resolves with the SHA-256 hash of the input string
     * as a hexadecimal string.
     */

  // First, the string needs to be encoded into bytes.
  // UTF-8 is a common encoding.
  const encoder = new TextEncoder();
  const data = encoder.encode(inputString);

  // Calculate the SHA-256 hash.
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // Convert the hash ArrayBuffer to a Base64Url string.
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // Check if Buffer is available (Node.js environment)
  if (typeof Buffer !== 'undefined' && Buffer.from) {
    return Buffer.from(hashArray).toString('base64url');
  } else {
    // Fallback for browser environment (manual Base64Url conversion)
    // Note: This is a simplified Base64Url conversion.
    // For a robust solution in the browser, consider a dedicated library
    // or a more comprehensive implementation of Base64Url encoding.
    const base64 = btoa(String.fromCharCode(...hashArray));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
}


/**
 * Ordena recursivamente las claves de un objeto y sus sub-objetos.
 * Arrays se manejan directamente sin ordenar sus elementos internos,
 * a menos que los elementos sean objetos que necesiten ser ordenados.
 *
 * @param obj El objeto a ordenar.
 * @returns Una nueva versión del objeto con las claves ordenadas.
 */
function sortObjectKeys(obj: unknown): unknown {
  // Manejo uniforme de valores null/undefined
  if (obj === undefined) {
    return null; // Convertir undefined a null para consistencia
  }
    
  if (typeof obj !== 'object' || obj === null) {
    // Si no es un objeto o es null, se devuelve tal cual.
    return obj;
  }

  if (obj instanceof Date) {
    return obj.toISOString()
  }

  if (Array.isArray(obj)) {
    // Filtrar undefined y convertirlo a null en arrays
    return obj.map(item => item === undefined ? null : sortObjectKeys(item));
  }

  // Se obtienen las claves del objeto y se ordenan alfabéticamente.
  const recordObj = obj as Record<string, unknown>
  const sortedKeys = Object.keys(recordObj).sort();
  const sortedObject: Record<string, unknown> = {};

  // Se construye un nuevo objeto con las claves ordenadas y sus valores,
  // aplicando la recursión a los valores que son objetos.
  for (const key of sortedKeys) {
    const val = recordObj[key];
    // Mantener null pero convertir undefined a null
    sortedObject[key] = val === undefined ? null : sortObjectKeys(val);
  }

  return sortedObject;
}

export async function HashJson(jsonObject: unknown): Promise<string> {
  const sortedJsonObject = sortObjectKeys(jsonObject ?? {});
  const jsonString = JSON.stringify(sortedJsonObject);

  const hash = await HashU(jsonString);

  return hash;
}